export const SECOND = 1000;
export const MINUTE = SECOND * 60;
export const HOUR = MINUTE * 60;
export const DAY = HOUR * 24;
export const WEEK = DAY * 7;

export const MRGE_GITHUB_REPO_OWNER = 'mrge-io';
export const EVALS_REPO_PREFIX = 'eval-';

export const SUPABASE_UPLOADS_BUCKET_NAME = 'uploads';

// Organizations allowed to show the "Fix with Cubic" button in PR comments
// Note: All entries are normalized to lowercase for case-insensitive matching
export const FIX_BUTTON_ORG_WHITELIST = [
  'myjunior',
  'distillco',
  'kestral-team',
  'qfex-org',
  'firecrawl',
  'udaan-com',
  'judilica',
];

// App name constants with dot notation access
export const APP_NAME = {
  PROD: 'cubic-dev-ai',
  DEV: 'cubic-dev-local',
  STAGING: 'cubic-staging',
} as const;

// Bot name constants
export const CUBIC_BOTS = {
  PROD: 'cubic-dev-ai[bot]',
  DEV: 'cubic-dev-local[bot]',
} as const;

// Array of all bot names for easy checking
export const CUBIC_BOT_NAMES: readonly string[] = [CUBIC_BOTS.PROD, CUBIC_BOTS.DEV];

// GitHub user IDs for cubic bot accounts (prod only for analytics)
export const CUBIC_BOT_GITHUB_USER_IDS: readonly string[] = ['191113872'];

// Query parameter names for referral system
export const REFERRAL_QUERY_PARAMS = {
  INVITE_CODE: 'invite',
} as const;

export const AUTH_QUERY_PARAMS = {
  SOURCE: 'source',
  CLI_CODE: 'cli_code',
  RETURN_TO: 'returnTo',
  RETRY_TO: 'retryTo',
  ATTEMPT_ID: 'attemptId',
  // Indicates which auth surface initiated GitHub OAuth so startup failures can return there.
  MODE: 'mode',
  // Query-backed auth startup error state rendered inline on auth pages after a failed retry.
  ERROR: 'authError',
  // Single-use code the desktop app redeems for its session. Not `code` or `access_token`, which
  // the browser Supabase client treats as an OAuth callback on every page load.
  DESKTOP_CODE: 'desktop_code',
} as const;

export const AUTH_FLOW = {
  WEB: 'web',
  DESKTOP: 'desktop',
  CLI: 'cli',
} as const;

export const AUTH_MODE = {
  SIGN_IN: 'sign-in',
  SIGN_UP: 'sign-up',
} as const;

export type AuthMode = (typeof AUTH_MODE)[keyof typeof AUTH_MODE];

/**
 * Stable auth startup error codes passed through the URL.
 * Keep these terse and non-sensitive because they are user-visible query params.
 */
export const AUTH_ERROR_CODES = {
  GITHUB_START_FAILED: 'github_start_failed',
  ACCOUNT_CONFLICT: 'account_conflict',
  // A desktop sign-in code that was never issued, expired, or was already used.
  DESKTOP_CODE_EXPIRED: 'desktop_code_expired',
} as const;

export type AuthErrorCode = (typeof AUTH_ERROR_CODES)[keyof typeof AUTH_ERROR_CODES];

// Trial period durations
export const TRIAL_DAYS = {
  STANDARD: 7,
  REFERRED: 14,
} as const;

/**
 * Settings deep-link action, e.g. `/settings?tab=usage&action=get-more-usage`.
 * The command palette pushes it; the target component opens its dialog and
 * strips the param so refresh/back doesn't reopen it.
 */
export const SETTINGS_ACTION_PARAM = 'action';
export const SETTINGS_ACTION_GET_MORE_USAGE = 'get-more-usage';

// Tab keys for the settings page (`/settings?tab=`)
export const SETTINGS_TABS = {
  GITHUB: 'github',
  NOTIFICATIONS: 'notifications',
  MEMBERS: 'members',
  BILLING: 'billing',
  USAGE: 'usage',
  INTEGRATIONS: 'integrations',
  API_CLI: 'api-cli',
  AUDIT_LOG: 'audit-log',
} as const;

export type SettingsTab = (typeof SETTINGS_TABS)[keyof typeof SETTINGS_TABS];

// Shared tab keys for the AI review page
export const AI_REVIEW_TABS = {
  SETUP: 'setup',
  AUTO_APPROVE: 'auto-approve',
  ULTRAREVIEW: 'ultrareview',
  CUSTOM_RULES: 'custom-rules',
  PR_DESCRIPTIONS: 'pr-descriptions',
  LEARNINGS: 'learnings',
  SENIOR_REVIEWERS: 'senior-reviewers',
  AUTO_FIX: 'auto-fix',
  /** Internal: shown only on installations where the viewer can use cubic code. */
  VERIFY: 'verify',
} as const;

export const AI_REVIEW_TAB_LABELS: Record<
  (typeof AI_REVIEW_TABS)[keyof typeof AI_REVIEW_TABS],
  string
> = {
  [AI_REVIEW_TABS.SETUP]: 'General review',
  [AI_REVIEW_TABS.AUTO_APPROVE]: 'Auto-approve',
  [AI_REVIEW_TABS.ULTRAREVIEW]: 'Ultrareview',
  [AI_REVIEW_TABS.CUSTOM_RULES]: 'Custom agents',
  [AI_REVIEW_TABS.PR_DESCRIPTIONS]: 'PR descriptions',
  [AI_REVIEW_TABS.LEARNINGS]: 'Learnings',
  [AI_REVIEW_TABS.SENIOR_REVIEWERS]: 'Senior reviewers',
  [AI_REVIEW_TABS.AUTO_FIX]: 'Auto-fix',
  [AI_REVIEW_TABS.VERIFY]: 'Verify',
};

// Repository learning sources
export const LEARNING_SOURCES = {
  HISTORICAL_SENIOR_REVIEWER_COMMENTS: 'historical_senior_reviewer_comments',
  INCREMENTAL_SENIOR_REVIEWER_COMMENTS: 'incremental_senior_reviewer_comments',
  USER_FEEDBACK_GITHUB: 'user-feedback-github',
  POST_MERGE_CODE_VALIDATED_FEEDBACK: 'POST_MERGE_CODE_VALIDATED_FEEDBACK',
  CODEBASE_SCAN_FEEDBACK: 'codebase-scan-feedback',
  USER_IMPORT: 'user-import',
} as const;

export const SENIOR_REVIEWER_LEARNING_SOURCES = [
  LEARNING_SOURCES.HISTORICAL_SENIOR_REVIEWER_COMMENTS,
  LEARNING_SOURCES.INCREMENTAL_SENIOR_REVIEWER_COMMENTS,
];

export type LearningSource = (typeof LEARNING_SOURCES)[keyof typeof LEARNING_SOURCES];
